import {
  createSlice,
  createAsyncThunk,
  type PayloadAction,
} from "@reduxjs/toolkit";

import { adminApiClient } from "../../services/apiClient";
import { toApiError } from "../../types/api";

import type {
  LearnerActivitySummary,
  UserActivitySegment,
} from "@lingoshade/domain";

interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: string;
  status: string;
  createdAt: string;
  registeredAt?: string | null;
  isAnonymous?: boolean;
  registrationUtmSource?: string | null;
  registrationUtmCampaign?: string | null;
  registrationReferrerHost?: string | null;
  activity?: LearnerActivitySummary;
  subscription?: {
    plan: string;
    status: string;
  };
}

interface SubscriptionEntry {
  id: string;
  plan: string | { id: string; name: string };
  status: string;
  startDate: string;
  endDate?: string;
}

interface UserDetails extends User {
  lastLoginAt?: string | null;
  emailVerified: boolean;
  profileCompleted: boolean;
  subscriptions?: SubscriptionEntry[];
}

interface UserStats {
  practicedTexts: number;
  listeningMinutes: number;
  completedDictations: number;
  dictationMinutes: number;
  savedWords: number;
  wordReviews: number;
  lastPracticeAt: string | null;
}

interface Pagination {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
}

interface AdminUsersState {
  users: User[];
  requestId: string | null;
  selectedUser: UserDetails | null;
  userStats: UserStats | null;
  loading: boolean;
  error: string | null;
  pagination: Pagination | null;
  deleteDialog: {
    open: boolean;
    user: User | null;
  };
  filters: {
    search?: string;
    role?: string;
    status?: string;
  };
}

const initialState: AdminUsersState = {
  users: [],
  requestId: null,
  selectedUser: null,
  userStats: null,
  loading: false,
  error: null,
  pagination: null,
  deleteDialog: {
    open: false,
    user: null,
  },
  filters: {},
};

// Async thunks
export const fetchUsers = createAsyncThunk<
  { users: User[]; pagination: Pagination },
  {
    page?: number;
    limit?: number;
    search?: string;
    role?: string;
    status?: string;
    segment?: UserActivitySegment;
    source?: string;
  },
  { rejectValue: string }
>(
  "adminUsers/fetchUsers",
  async (
    params = {},
    { rejectWithValue, signal },
  ): Promise<
    | { users: User[]; pagination: Pagination }
    | ReturnType<typeof rejectWithValue>
  > => {
    try {
      const response = await adminApiClient.get<{
        users: User[];
        pagination: {
          page: number;
          pages: number;
          total: number;
          limit: number;
        };
      }>("/admin/users", { params, signal });
      const { page, pages, total, limit } = response.data.pagination;
      return {
        users: response.data.users,
        pagination: {
          currentPage: page,
          totalPages: pages,
          totalItems: total,
          itemsPerPage: limit,
        },
      };
    } catch (error: unknown) {
      return rejectWithValue(toApiError(error).message);
    }
  },
);

export const fetchUserDetails = createAsyncThunk<
  { user: UserDetails; stats: UserStats },
  string,
  { rejectValue: string }
>("adminUsers/fetchUserDetails", async (userId, { rejectWithValue }) => {
  try {
    const [userResponse, statsResponse] = await Promise.all([
      adminApiClient.get(`/admin/users/${userId}`),
      adminApiClient.get(`/admin/users/${userId}/stats`),
    ]);

    return {
      user: userResponse.data.user,
      stats: statsResponse.data.stats,
    };
  } catch (error: unknown) {
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message);
  }
});

export const deleteUser = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>("adminUsers/deleteUser", async (userId, { rejectWithValue }) => {
  try {
    await adminApiClient.delete(`/admin/users/${userId}`);
    return userId;
  } catch (error: unknown) {
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message);
  }
});

export const updateUser = createAsyncThunk<
  UserDetails,
  { userId: string; data: Partial<User> },
  { rejectValue: string }
>("adminUsers/updateUser", async ({ userId, data }, { rejectWithValue }) => {
  try {
    const response = await adminApiClient.patch(`/admin/users/${userId}`, data);
    return response.data.user;
  } catch (error: unknown) {
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message);
  }
});

export const fetchUserSubscriptions = createAsyncThunk<
  SubscriptionEntry[],
  string,
  { rejectValue: string }
>("adminUsers/fetchUserSubscriptions", async (userId, { rejectWithValue }) => {
  try {
    const response = await adminApiClient.get(
      `/admin/users/${userId}/subscriptions`,
    );
    return response.data.subscriptions;
  } catch (error: unknown) {
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message);
  }
});

export const addSubscription = createAsyncThunk<
  SubscriptionEntry,
  { userId: string; plan: string; duration: number },
  { rejectValue: string }
>(
  "adminUsers/addSubscription",
  async ({ userId, plan, duration }, { rejectWithValue }) => {
    try {
      const response = await adminApiClient.post(
        `/admin/users/${userId}/subscription`,
        {
          plan,
          duration,
        },
      );
      return response.data.subscription;
    } catch (error: unknown) {
      const apiError = toApiError(error);
      return rejectWithValue(apiError.message);
    }
  },
);

export const cancelSubscription = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>(
  "adminUsers/cancelSubscription",
  async (subscriptionId, { rejectWithValue }) => {
    try {
      await adminApiClient.patch(
        `/admin/subscriptions/${subscriptionId}/cancel`,
      );
      return subscriptionId;
    } catch (error: unknown) {
      const apiError = toApiError(error);
      return rejectWithValue(apiError.message);
    }
  },
);

const adminUsersSlice = createSlice({
  name: "adminUsers",
  initialState,
  reducers: {
    setFilters: (
      state,
      action: PayloadAction<Partial<AdminUsersState["filters"]>>,
    ): void => {
      state.filters = { ...state.filters, ...action.payload };
    },
    openDeleteDialog: (state, action: PayloadAction<User>): void => {
      state.deleteDialog = {
        open: true,
        user: action.payload,
      };
    },
    closeDeleteDialog: (state): void => {
      state.deleteDialog = {
        open: false,
        user: null,
      };
    },
    clearError: (state): void => {
      state.error = null;
    },
    clearSelectedUser: (state): void => {
      state.selectedUser = null;
      state.userStats = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch users
      .addCase(fetchUsers.pending, (state, action): void => {
        state.requestId = action.meta.requestId;
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action): void => {
        if (state.requestId !== action.meta.requestId) return;
        state.loading = false;
        state.users = action.payload.users;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchUsers.rejected, (state, action): void => {
        if (state.requestId !== action.meta.requestId) return;
        state.loading = false;
        if (!action.meta.aborted)
          state.error = action.payload || "Failed to fetch users";
      })
      // Fetch user details
      .addCase(fetchUserDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedUser = action.payload.user;
        state.userStats = action.payload.stats;
      })
      .addCase(fetchUserDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch user details";
      })
      // Delete user
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.users = state.users.filter((user) => user.id !== action.payload);
        state.deleteDialog = { open: false, user: null };
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.error = action.payload || "Failed to delete user";
      })
      // Update user
      .addCase(updateUser.fulfilled, (state, action) => {
        state.selectedUser = action.payload;
        const index = state.users.findIndex(
          (user) => user.id === action.payload.id,
        );
        if (index !== -1) {
          state.users[index] = action.payload;
        }
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.error = action.payload || "Failed to update user";
      })
      // Fetch user subscriptions
      .addCase(fetchUserSubscriptions.fulfilled, (state, action) => {
        if (state.selectedUser) {
          state.selectedUser.subscriptions = action.payload;
        }
      })
      .addCase(fetchUserSubscriptions.rejected, (state, action) => {
        state.error = action.payload || "Failed to fetch subscriptions";
      })
      // Add subscription
      .addCase(addSubscription.fulfilled, (state, action) => {
        if (state.selectedUser?.subscriptions) {
          state.selectedUser.subscriptions.push(action.payload);
        }
      })
      .addCase(addSubscription.rejected, (state, action) => {
        state.error = action.payload || "Failed to add subscription";
      })
      // Cancel subscription
      .addCase(cancelSubscription.fulfilled, (state, action) => {
        if (state.selectedUser?.subscriptions) {
          const index = state.selectedUser.subscriptions.findIndex(
            (sub) => sub.id === action.payload,
          );
          if (index !== -1) {
            state.selectedUser.subscriptions[index].status = "CANCELLED";
          }
        }
      })
      .addCase(cancelSubscription.rejected, (state, action) => {
        state.error = action.payload || "Failed to cancel subscription";
      });
  },
});

export const {
  setFilters,
  openDeleteDialog,
  closeDeleteDialog,
  clearError,
  clearSelectedUser,
} = adminUsersSlice.actions;

// Export with aliases for compatibility
export { fetchUsers as fetchAdminUsers };
export { deleteUser as deleteAdminUser };

export default adminUsersSlice.reducer;
